import { useEffect, useMemo, useRef, useState } from "react";
import {
  createChart,
  CandlestickSeries,
  LineSeries,
  HistogramSeries,
  ColorType,
  LineStyle,
} from "lightweight-charts";
import {
  Maximize2,
  Minimize2,
  RotateCcw,
  Settings2,
  Eye,
  EyeOff,
  X,
  Plus,
} from "lucide-react";
import { API, authHeader } from "@/lib/api";

const INTERVALS = [
  ["1m", "1M"],
  ["5m", "5M"],
  ["15m", "15M"],
  ["1h", "1H"],
  ["4h", "4H"],
];

const INDICATOR_TYPES = [
  ["SMA", "SMA"],
  ["EMA", "EMA"],
  ["WMA", "WMA"],
  ["VWMA", "VWMA"],
  ["BB", "Bollinger"],
  ["RSI", "RSI"],
  ["MACD", "MACD"],
  ["ATR", "ATR"],
  ["VOL", "Volume"],
];

const num = (v) => Number.isFinite(Number(v)) ? Number(v) : 0;

const sma = (values, period) => {
  const out = [];
  let sum = 0;
  for (let i = 0; i < values.length; i += 1) {
    sum += num(values[i]);
    if (i >= period) sum -= num(values[i - period]);
    out.push(i >= period - 1 ? sum / period : null);
  }
  return out;
};

const ema = (values, period) => {
  const out = [];
  const k = 2 / (period + 1);
  let prev = null;
  for (let i = 0; i < values.length; i += 1) {
    const value = num(values[i]);
    if (i < period - 1) {
      out.push(null);
      continue;
    }
    if (prev == null) {
      let sum = 0;
      for (let j = i - period + 1; j <= i; j += 1) sum += num(values[j]);
      prev = sum / period;
    } else {
      prev = value * k + prev * (1 - k);
    }
    out.push(prev);
  }
  return out;
};

const wma = (values, period) => {
  const out = [];
  const denom = (period * (period + 1)) / 2;
  for (let i = 0; i < values.length; i += 1) {
    if (i < period - 1) {
      out.push(null);
      continue;
    }
    let sum = 0;
    for (let j = 0; j < period; j += 1) {
      sum += num(values[i - j]) * (period - j);
    }
    out.push(sum / denom);
  }
  return out;
};

const vwma = (candles, period) => {
  const out = [];
  for (let i = 0; i < candles.length; i += 1) {
    if (i < period - 1) {
      out.push(null);
      continue;
    }
    let pv = 0;
    let vol = 0;
    for (let j = i - period + 1; j <= i; j += 1) {
      const c = candles[j];
      const v = num(c.volume || 1);
      pv += num(c.close) * v;
      vol += v;
    }
    out.push(vol ? pv / vol : num(candles[i].close));
  }
  return out;
};

const rsi = (values, period = 14) => {
  const out = Array(values.length).fill(null);
  if (values.length <= period) return out;

  let gain = 0;
  let loss = 0;

  for (let i = 1; i <= period; i += 1) {
    const diff = num(values[i]) - num(values[i - 1]);
    if (diff >= 0) gain += diff;
    else loss -= diff;
  }

  let avgGain = gain / period;
  let avgLoss = loss / period;

  out[period] = avgLoss === 0 ? 100 : 100 - (100 / (1 + avgGain / avgLoss));

  for (let i = period + 1; i < values.length; i += 1) {
    const diff = num(values[i]) - num(values[i - 1]);
    const g = diff > 0 ? diff : 0;
    const l = diff < 0 ? -diff : 0;

    avgGain = ((avgGain * (period - 1)) + g) / period;
    avgLoss = ((avgLoss * (period - 1)) + l) / period;

    out[i] = avgLoss === 0
      ? 100
      : 100 - (100 / (1 + avgGain / avgLoss));
  }

  return out;
};

const trueRanges = (candles) => {
  const out = [];
  for (let i = 0; i < candles.length; i += 1) {
    const c = candles[i];
    if (i === 0) {
      out.push(num(c.high) - num(c.low));
      continue;
    }
    const prev = num(candles[i - 1].close);
    out.push(Math.max(
      num(c.high) - num(c.low),
      Math.abs(num(c.high) - prev),
      Math.abs(num(c.low) - prev),
    ));
  }
  return out;
};

const atr = (candles, period = 14) => sma(trueRanges(candles), period);

const seriesData = (candles, values) =>
  candles
    .map((c, i) => values[i] == null ? null : ({ time: c.time, value: values[i] }))
    .filter(Boolean);

const formatPrice = (v, digits = 2) =>
  Number.isFinite(Number(v)) ? Number(v).toFixed(digits) : "—";

export const CandleChart = ({ symbol, height = 560, title }) => {
  const ref = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const indicatorRefs = useRef([]);
  const [interval, setInterval_] = useState("15m");
  const [candles, setCandles] = useState([]);
  const [meta, setMeta] = useState(null);
  const [chartError, setChartError] = useState("");
  const [fullscreen, setFullscreen] = useState(false);
  const [showIndicators, setShowIndicators] = useState(false);
  const [crosshair, setCrosshair] = useState(null);
  const [indicators, setIndicators] = useState([]);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const digits = meta?.digits || 2;

  const addIndicator = (type) => {
    const defaults = {
      SMA: { period: 20 },
      EMA: { period: 50 },
      WMA: { period: 20 },
      VWMA: { period: 20 },
      BB: { period: 20, std: 2 },
      RSI: { period: 14 },
      MACD: { fast: 12, slow: 26, signal: 9 },
      ATR: { period: 14 },
      VOL: { period: 20 },
    };

    setIndicators((prev) => [
      ...prev,
      {
        id: `${type}-${Date.now()}-${Math.random()}`,
        type,
        visible: true,
        ...defaults[type],
      },
    ]);
  };

  const removeIndicator = (id) => {
    setIndicators((prev) => prev.filter((x) => x.id !== id));
  };

  const updateIndicator = (id, patch) => {
    setIndicators((prev) =>
      prev.map((x) => x.id === id ? { ...x, ...patch } : x)
    );
  };

  const clearIndicators = () => setIndicators([]);

  const fit = () => {
    chartRef.current?.timeScale().fitContent();
  };

  const reset = () => {
    if (!chartRef.current) return;
    chartRef.current.timeScale().fitContent();
    chartRef.current.priceScale("right").applyOptions({
      autoScale: true,
    });
  };

  const toggleFullscreen = () => {
    setFullscreen((v) => !v);
  };

  useEffect(() => {
    if (!ref.current) return;

    let chart;

    try {
      chart = createChart(ref.current, {
        width: ref.current.clientWidth,
        height,
        layout: {
          background: {
            type: ColorType.Solid,
            color: "#0b1017",
          },
          textColor: "#a6adb8",
          fontFamily: "JetBrains Mono, monospace",
          fontSize: 10,
        },
        grid: {
          vertLines: { color: "rgba(255,255,255,0.04)" },
          horzLines: { color: "rgba(255,255,255,0.04)" },
        },
        rightPriceScale: {
          borderColor: "rgba(255,255,255,0.12)",
          autoScale: true,
        },
        timeScale: {
          borderColor: "rgba(255,255,255,0.12)",
          timeVisible: true,
          secondsVisible: false,
          rightOffset: 8,
          barSpacing: 8,
          minBarSpacing: 2,
        },
        crosshair: {
          mode: 0,
          vertLine: {
            color: "rgba(255,255,255,0.35)",
            width: 1,
            style: LineStyle.Dashed,
          },
          horzLine: {
            color: "rgba(255,255,255,0.35)",
            width: 1,
            style: LineStyle.Dashed,
          },
        },
        handleScale: {
          mouseWheel: true,
          pinch: true,
          axisPressedMouseMove: true,
        },
        handleScroll: {
          mouseWheel: true,
          pressedMouseMove: true,
          horzTouchDrag: true,
          vertTouchDrag: true,
        },
        localization: {
          locale: "id-ID",
        },
      });

      const series = chart.addSeries(CandlestickSeries, {
        upColor: "#00E676",
        downColor: "#FF4C4C",
        borderUpColor: "#00E676",
        borderDownColor: "#FF4C4C",
        wickUpColor: "#00E676",
        wickDownColor: "#FF4C4C",
      });

      chartRef.current = chart;
      seriesRef.current = series;
      setChartError("");

      const resize = () => {
        if (!ref.current) return;
        chart.applyOptions({
          width: ref.current.clientWidth,
          height: fullscreen ? window.innerHeight - 110 : height,
        });
      };

      const ro = new ResizeObserver(resize);
      ro.observe(ref.current);

      const onCrosshair = (param) => {
        if (!param || !param.time || !param.seriesData) {
          setCrosshair(null);
          return;
        }

        const data = param.seriesData.get(series);

        if (!data) {
          setCrosshair(null);
          return;
        }

        setCrosshair({
          time: param.time,
          open: data.open,
          high: data.high,
          low: data.low,
          close: data.close,
        });
      };

      chart.subscribeCrosshairMove(onCrosshair);

      return () => {
        ro.disconnect();
        chart.unsubscribeCrosshairMove(onCrosshair);
        chart.remove();
        chartRef.current = null;
        seriesRef.current = null;
      };
    } catch (err) {
      console.error("[CandleChart] init error:", err);
      setChartError(err?.message || String(err));
    }
  }, [height, fullscreen]);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        setChartError("");

        const resp = await fetch(
          `${API}/market/candles?symbol=${symbol}&interval=${interval}`,
          { headers: authHeader() }
        );

        if (!resp.ok) {
          throw new Error(`HTTP ${resp.status}`);
        }

        const data = await resp.json();

        if (!alive) return;

        const normalized = (data.candles || []).map((c) => ({
          ...c,
          time: c.time,
        }));

        setCandles(normalized);
        setMeta({
          o: normalized[0]?.open,
          h: normalized.length
            ? Math.max(...normalized.map((c) => num(c.high)))
            : null,
          l: normalized.length
            ? Math.min(...normalized.map((c) => num(c.low)))
            : null,
          c: normalized[normalized.length - 1]?.close,
          digits: data.digits || 2,
        });
      } catch (err) {
        if (!alive) return;
        console.error("[CandleChart] load error:", err);
        setChartError(err?.message || String(err));
      }
    };

    load();

    const timer = setInterval(load, 30000);

    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [symbol, interval]);

  useEffect(() => {
    if (!seriesRef.current || !candles.length) return;

    try {
      seriesRef.current.setData(
        candles.map((c) => ({
          time: c.time,
          open: num(c.open),
          high: num(c.high),
          low: num(c.low),
          close: num(c.close),
        }))
      );

      chartRef.current?.timeScale().fitContent();
    } catch (err) {
      console.error("[CandleChart] candle render error:", err);
      setChartError(err?.message || String(err));
    }
  }, [candles]);

  const computedIndicators = useMemo(() => {
    const closes = candles.map((c) => num(c.close));

    return indicators.map((ind) => {
      if (ind.type === "SMA") {
        return {
          ...ind,
          values: sma(closes, Math.max(1, Number(ind.period) || 20)),
        };
      }

      if (ind.type === "EMA") {
        return {
          ...ind,
          values: ema(closes, Math.max(1, Number(ind.period) || 50)),
        };
      }

      if (ind.type === "WMA") {
        return {
          ...ind,
          values: wma(closes, Math.max(1, Number(ind.period) || 20)),
        };
      }

      if (ind.type === "VWMA") {
        return {
          ...ind,
          values: vwma(candles, Math.max(1, Number(ind.period) || 20)),
        };
      }

      if (ind.type === "BB") {
        const period = Math.max(1, Number(ind.period) || 20);
        const basis = sma(closes, period);
        const upper = [];
        const lower = [];

        for (let i = 0; i < closes.length; i += 1) {
          if (i < period - 1) {
            upper.push(null);
            lower.push(null);
            continue;
          }

          let sum = 0;
          for (let j = i - period + 1; j <= i; j += 1) {
            sum += Math.pow(closes[j] - basis[i], 2);
          }

          const sd = Math.sqrt(sum / period);
          const mult = Number(ind.std) || 2;

          upper.push(basis[i] + sd * mult);
          lower.push(basis[i] - sd * mult);
        }

        return {
          ...ind,
          values: basis,
          upper,
          lower,
        };
      }

      if (ind.type === "RSI") {
        return {
          ...ind,
          values: rsi(closes, Math.max(1, Number(ind.period) || 14)),
        };
      }

      if (ind.type === "ATR") {
        return {
          ...ind,
          values: atr(candles, Math.max(1, Number(ind.period) || 14)),
        };
      }

      if (ind.type === "MACD") {
        const fast = ema(closes, Number(ind.fast) || 12);
        const slow = ema(closes, Number(ind.slow) || 26);
        const macd = closes.map((_, i) =>
          fast[i] == null || slow[i] == null ? null : fast[i] - slow[i]
        );
        const signal = ema(
          macd.map((v) => v == null ? 0 : v),
          Number(ind.signal) || 9
        );
        const histogram = macd.map((v, i) =>
          v == null || signal[i] == null ? null : v - signal[i]
        );

        return {
          ...ind,
          values: macd,
          signal,
          histogram,
        };
      }

      if (ind.type === "VOL") {
        return {
          ...ind,
          values: candles.map((c) => num(c.volume)),
        };
      }

      return ind;
    });
  }, [candles, indicators]);

  useEffect(() => {
    if (!chartRef.current) return;

    indicatorRefs.current.forEach((item) => {
      try {
        chartRef.current.removeSeries(item);
      } catch {
        // already removed
      }
    });

    indicatorRefs.current = [];

    computedIndicators.forEach((ind) => {
      if (!ind.visible) return;

      if (["SMA", "EMA", "WMA", "VWMA"].includes(ind.type)) {
        const line = chartRef.current.addSeries(LineSeries, {
          lineWidth: 1,
          priceLineVisible: false,
          lastValueVisible: false,
        });

        line.setData(seriesData(candles, ind.values));
        indicatorRefs.current.push(line);
      }

      if (ind.type === "BB") {
        [ind.upper, ind.values, ind.lower].forEach((values) => {
          const line = chartRef.current.addSeries(LineSeries, {
            lineWidth: 1,
            priceLineVisible: false,
            lastValueVisible: false,
          });

          line.setData(seriesData(candles, values));
          indicatorRefs.current.push(line);
        });
      }

      if (ind.type === "RSI" || ind.type === "ATR") {
        const line = chartRef.current.addSeries(LineSeries, {
          lineWidth: 1,
          priceLineVisible: false,
          lastValueVisible: false,
        });

        line.setData(seriesData(candles, ind.values));
        indicatorRefs.current.push(line);
      }

      if (ind.type === "MACD") {
        const macdLine = chartRef.current.addSeries(LineSeries, {
          lineWidth: 1,
          priceLineVisible: false,
          lastValueVisible: false,
        });

        macdLine.setData(seriesData(candles, ind.values));
        indicatorRefs.current.push(macdLine);

        const signalLine = chartRef.current.addSeries(LineSeries, {
          lineWidth: 1,
          priceLineVisible: false,
          lastValueVisible: false,
        });

        signalLine.setData(seriesData(candles, ind.signal));
        indicatorRefs.current.push(signalLine);

        const histogram = chartRef.current.addSeries(HistogramSeries, {
          priceLineVisible: false,
          lastValueVisible: false,
        });

        histogram.setData(
          candles
            .map((c, i) =>
              ind.histogram[i] == null
                ? null
                : {
                    time: c.time,
                    value: ind.histogram[i],
                  }
            )
            .filter(Boolean)
        );

        indicatorRefs.current.push(histogram);
      }

      if (ind.type === "VOL") {
        const histogram = chartRef.current.addSeries(HistogramSeries, {
          priceLineVisible: false,
          lastValueVisible: false,
        });

        histogram.setData(
          candles.map((c, i) => ({
            time: c.time,
            value: num(c.volume),
          }))
        );

        indicatorRefs.current.push(histogram);
      }
    });

    return () => {};
  }, [computedIndicators, candles]);

  const chartHeight = fullscreen
    ? "calc(100vh - 105px)"
    : `${height}px`;

  return (
    <div
      className={`min-w-0 w-full max-w-full overflow-hidden border border-white/10 bg-[#0b1017] ${
        fullscreen
          ? "fixed inset-0 z-[100] flex flex-col"
          : ""
      }`}
      data-testid="candle-chart"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/10 px-3 py-2">
        <div className="flex flex-wrap items-center gap-1">
          {INTERVALS.map(([v, l]) => (
            <button
              key={v}
              onClick={() => setInterval_(v)}
              data-testid={`tf-${l.toLowerCase()}`}
              className={`px-2.5 py-1 font-mono text-[10px] font-bold transition-colors ${
                interval === v
                  ? "bg-white/10 text-white"
                  : "text-mute-dim hover:text-white"
              }`}
            >
              {l}
            </button>
          ))}

          <span className="mx-1 h-4 w-px bg-white/10" />

          <button
            onClick={fit}
            className="px-2 py-1 font-mono text-[10px] text-mute-dim hover:text-white"
            title="Fit chart"
          >
            FIT
          </button>

          <button
            onClick={reset}
            className="px-2 py-1 text-mute-dim hover:text-white"
            title="Reset chart"
          >
            <RotateCcw size={12} />
          </button>

          <button
            onClick={() => setShowIndicators((v) => !v)}
            className={`px-2 py-1 ${
              showIndicators ? "text-white" : "text-mute-dim"
            } hover:text-white`}
            title="Indicators"
          >
            <Settings2 size={12} />
          </button>

          <button
            onClick={toggleFullscreen}
            className="px-2 py-1 text-mute-dim hover:text-white"
            title="Fullscreen"
          >
            {fullscreen ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
          </button>
        </div>

        <div className="flex min-w-0 items-center gap-3 font-mono text-[10px] text-mute-dim">
          {crosshair ? (
            <span className="hidden lg:inline whitespace-nowrap">
              O{formatPrice(crosshair.open, digits)}
              {" "}
              H{formatPrice(crosshair.high, digits)}
              {" "}
              L{formatPrice(crosshair.low, digits)}
              {" "}
              C{formatPrice(crosshair.close, digits)}
            </span>
          ) : meta ? (
            <span className="hidden sm:inline whitespace-nowrap">
              {symbol} · {interval.toUpperCase()} · O{formatPrice(meta.o, digits)}
              {" "}H{formatPrice(meta.h, digits)}
              {" "}L{formatPrice(meta.l, digits)}
              {" "}C{formatPrice(meta.c, digits)}
            </span>
          ) : null}
        </div>
      </div>

      {showIndicators && (
        <div className="border-b border-white/10 bg-[#0d131c] px-3 py-2">
          <div className="flex flex-wrap items-center gap-2">
            {INDICATOR_TYPES.map(([value, label]) => (
              <button
                key={value}
                onClick={() => addIndicator(value)}
                className="flex items-center gap-1 border border-white/10 px-2 py-1 font-mono text-[9px] text-mute-dim hover:border-white/20 hover:text-white"
              >
                <Plus size={10} />
                {label}
              </button>
            ))}

            {indicators.length > 0 && (
              <button
                onClick={clearIndicators}
                className="ml-auto px-2 py-1 font-mono text-[9px] text-sell hover:text-white"
              >
                CLEAR
              </button>
            )}
          </div>

          {indicators.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {indicators.map((ind) => (
                <div
                  key={ind.id}
                  className="flex items-center gap-2 border border-white/10 bg-black/10 px-2 py-1"
                >
                  <span className="font-mono text-[9px] text-white">
                    {ind.type}
                  </span>

                  {["SMA", "EMA", "WMA", "VWMA", "RSI", "ATR"].includes(ind.type) && (
                    <input
                      type="number"
                      min="1"
                      max="500"
                      value={ind.period}
                      onChange={(e) =>
                        updateIndicator(ind.id, {
                          period: Math.max(1, Number(e.target.value) || 1),
                        })
                      }
                      className="w-12 border border-white/10 bg-transparent px-1 py-0.5 font-mono text-[9px] text-white outline-none"
                    />
                  )}

                  {ind.type === "BB" && (
                    <>
                      <input
                        type="number"
                        min="1"
                        max="200"
                        value={ind.period}
                        onChange={(e) =>
                          updateIndicator(ind.id, {
                            period: Math.max(1, Number(e.target.value) || 1),
                          })
                        }
                        className="w-12 border border-white/10 bg-transparent px-1 py-0.5 font-mono text-[9px] text-white outline-none"
                      />
                      <input
                        type="number"
                        min="0.1"
                        max="10"
                        step="0.1"
                        value={ind.std}
                        onChange={(e) =>
                          updateIndicator(ind.id, {
                            std: Number(e.target.value) || 2,
                          })
                        }
                        className="w-12 border border-white/10 bg-transparent px-1 py-0.5 font-mono text-[9px] text-white outline-none"
                      />
                    </>
                  )}

                  {ind.type === "MACD" && (
                    <span className="font-mono text-[8px] text-mute-dim">
                      {ind.fast}/{ind.slow}/{ind.signal}
                    </span>
                  )}

                  <button
                    onClick={() =>
                      updateIndicator(ind.id, { visible: !ind.visible })
                    }
                    className="text-mute-dim hover:text-white"
                    title={ind.visible ? "Hide" : "Show"}
                  >
                    {ind.visible ? <Eye size={11} /> : <EyeOff size={11} />}
                  </button>

                  <button
                    onClick={() => removeIndicator(ind.id)}
                    className="text-mute-dim hover:text-sell"
                    title="Remove"
                  >
                    <X size={11} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div
        ref={ref}
        className="min-w-0 w-full max-w-full overflow-hidden"
        style={{ height: chartHeight }}
      />

      {chartError && (
        <div className="border-t border-sell/20 bg-sell/5 px-3 py-3 font-mono text-[10px] text-sell break-all">
          CHART ERROR: {chartError}
        </div>
      )}

      {title && (
        <p className="border-t border-white/5 px-3 py-1.5 font-mono text-[9px] text-mute-dim">
          {title}
        </p>
      )}
    </div>
  );
};
